import {inject, toRaw, nextTick} from 'vue'
import {useI18n} from 'vue-i18n'
import {useStatefulComposable} from './auth.js'
import LxUtils from '../lib/lxutils.js'

const {singleCheckForExpiredToken} = useStatefulComposable()

/**
 * Mapping for error message to property in error details object
 *
 * example: message = 'invalid_type' -> 'details.expected'
 */
const errorMapper = {
  // json validation errors
  string_gte: 'min',
  string_lte: 'max',
  number_gte: 'min',
  number_gt: 'min',
  number_lte: 'max',
  number_lt: 'max',
  format: 'format',
  pattern: 'pattern',
  unique: 'type',
  array_max_items: 'max',
  array_min_items: 'min',
  enum: 'allowed',
  invalid_type: 'expected',
  missing_dependency: 'dependency',
  required: 'property',

  // other validation errors
  error_duplicate_partner: 'value'
}

export const useLxHelperFunctions = () => {
  const $snackbar = inject('$snackbar')
  const formsMetaData = inject('$formsMetaData')
  const {t} = useI18n()

  /**
   * Calculates query param from options
   *
   * @param {Object} params
   * @param {Object} options
   * @returns {{opts: {locale: string}, count: boolean}}
   */
  const getQueryParam = (params, options) => {
    // init params
    options = options || {}
    params = params || {}
    params.opts = params.opts || {}
    params.query = params.query || {}

    // merge options to params
    if (options.sort) {
      params.opts.sort = options.sort
    }
    if (options.filter) {
      params.query = options.filter
    }
    if (options.skip || options.skip === 0) {
      params.opts.skip = options.skip
    }
    if (options.limit) {
      params.opts.limit = options.limit
    }
    if (options.fields) {
      params.opts.fields = options.fields
    }

    // init return value
    let localParams = {
      count: true,
      opts: {
        locale: 'de'
      }
    }

    // count
    if (typeof params.count === 'boolean') {
      localParams.count = params.count
    }

    // paging
    if (!isNaN(params.opts.limit)) {
      localParams.opts['limit'] = params.opts.limit
    }
    if (!isNaN(params.opts.skip)) {
      localParams.opts['skip'] = params.opts.skip
    }

    // sorting
    if (typeof params.opts.sort === 'object' && Object.keys(params.opts.sort).length > 0) {
      localParams.opts['sort'] = params.opts.sort
    }

    // filter
    if (typeof params.query === 'object' && Object.keys(params.query).length > 0) {
      localParams['query'] = params.query
    }

    // fields
    if (typeof params.opts.fields === 'object' && Object.keys(params.opts.fields).length > 0) {
      localParams.opts['fields'] = params.opts.fields
    }

    // returns config parameter
    return localParams
  }

  /**
   * Converts values of object to json string if value is nested object or array
   *
   * @param {object} obj
   * @returns {object}
   */
  const processQueryParams = (obj) => {
    Object.keys(obj).forEach(key => {
      if (typeof obj[key] === 'object' || Array.isArray(obj[key])) {
        obj[key] = JSON.stringify(obj[key])
      }
    })

    return obj
  }

  /**
   * Checks if two nested object values are te same
   *
   * @param object1
   * @param object2
   * @returns {boolean}
   */
  const deepEqual = (object1, object2) => {
    if (!object1 || !object2) {
      return false
    }

    const keys1 = Object.keys(object1)
    const keys2 = Object.keys(object2)

    if (keys1.length !== keys2.length) {
      return false
    }

    for (const key of keys1) {
      const val1 = object1[key]
      const val2 = object2[key]
      const areObjects = isObject(val1) && isObject(val2)

      // recursion
      if (areObjects && !deepEqual(val1, val2) || !areObjects && val1 !== val2) {
        return false
      }
    }

    return true
  }

  /**
   * Checks if value is type object
   *
   * @param {*} data The data to check if its of type object
   * @returns {boolean}
   */
  function isObject(data) {
    return data != null && typeof data === 'object'
  }

  /**
   * Creates id
   *
   * @param length
   * @returns {string}
   */
  function makeId(length) {
    let result = ''
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    let counter = 0
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
      counter += 1
    }
    return result
  }

  /**
   * Returns array of days from string value ('3,6-8,12' --> [3,6,7,8,12])
   *
   * @param {string} value the days value
   * @returns {[number]}
   */
  function calculateDays(value) {
    if (!value) {
      return []
    }

    let days = []
    value = value.replace(/\s+/g, '')

    // replace '.' with ','
    value = value.replace(/\./, ",")

    value.split(',').forEach(item => {
      if (item.indexOf('-') >= 0) {
        // range for instance 1-2
        let parts = item.split('-')

        if (parts.length === 2) {
          let tmpPart0 = parseInt(parts[0], 10)
          let tmpPart1 = parseInt(parts[1], 10)
          let i = tmpPart0
          let length = tmpPart1

          if (tmpPart0 > tmpPart1) {
            i = tmpPart1
            length = tmpPart0
          }
          for (i; i <= length; i++) {
            if (isFinite(i)) {
              days.push(i)
            }
          }
        }
      } else {
        // single value
        let n = parseInt(item, 10)
        if (isFinite(n)) {
          days.push(n)
        }
      }
    })

    return days.sort((a, b) => {
      return a < b ? -1 : 1
    })
  }

  /**
   * Calculates cycle index by date for a break
   *
   * @param {Array} schemaDetails list of schema details
   * @param {Array} breaks list of breaks
   * @param {Date} breakStartDate date of break
   * @returns {number} returns cycle index
   */
  function calculateCycleIndexByDate(schemaDetails, breaks, breakStartDate) {
    breaks = breaks || []
    breakStartDate = new Date(breakStartDate)
    breakStartDate = new Date(new Date(breakStartDate.setDate(breakStartDate.getDate())).setHours(0, 0, 0, 0))

    let schemaDetailsIndex = 0
    let cycleIndex = schemaDetails[schemaDetailsIndex].cycle_start
    let breakIndex = 0

    // start date of first details
    let date = new Date(schemaDetails[schemaDetailsIndex].date_start)

    // add first length of cycle for range
    date = new Date(new Date(date.setDate(date.getDate() + schemaDetails[schemaDetailsIndex].length_of_cycle)).setHours(0, 0, 0, 0))

    // check breaks are in first cycle range
    for (breakIndex; breakIndex < breaks.length; breakIndex++) {
      if (new Date(breaks[breakIndex].date_start) < date) {
        date = new Date(date.setDate(date.getDate() + breaks[breakIndex].length_of_break))
      } else {
        break // break
      }
    }

    // check next cycles
    while (date <= breakStartDate) {
      // next cycle
      cycleIndex++

      // check for next details item
      if (cycleIndex > (schemaDetailsIndex === 0 ? schemaDetails[schemaDetailsIndex].number_of_cycles : schemaDetails[schemaDetailsIndex].cycle_start + schemaDetails[schemaDetailsIndex].number_of_cycles - 1)) {
        schemaDetailsIndex++

        // check length of details array
        if (schemaDetailsIndex > schemaDetails.length) {
          return cycleIndex
        }
      }

      // calc new date
      date = new Date(new Date(date.setDate(date.getDate() + schemaDetails[schemaDetailsIndex].length_of_cycle)).setHours(0, 0, 0, 0))

      // check breaks are in cycle range
      for (breakIndex; breakIndex < breaks.length; breakIndex++) {
        if (new Date(breaks[breakIndex].date_start) < date) {
          date = new Date(date.setDate(date.getDate() + breaks[breakIndex].length_of_break))
        } else {
          break // break
        }
      }
    }

    return cycleIndex
  }

  /**
   * Check if arrays have equal objects
   *
   * @param arr1
   * @param arr2
   */
  function checkArrayEquals(arr1, arr2) {
    return Array.isArray(arr1) && Array.isArray(arr2) && arr1.length === arr2.length && arr1.every((val, index) => deepEqual(val, arr2[index]))
  }

  /**
   * Process server error messages or server validation errors
   *
   * @param {function, null} fnSetServerError The function for displaying server errors
   * @param {object} err The error object
   * @param {object} options The additional options
   * @param {string, undefined} options.pathPrefixExclude The additional path to exclude from field path
   * @param {string, undefined} options.pathPrefixInclude The additional path to include from field path
   * @param {boolean, undefined} options.hideSnackbar The option for not displaying snackbar
   */
  function processErrors(fnSetServerError, err, options = {}) {

    function processArrayPath(pathValue, pathOptions) {
      // transform path value
      // example: 'settings.3.infusion_protocol_items.0.text' -> 'settings[3].infusion_protocol_items[0].text'
      let regex = /\.\d+\./
      let result = regex.exec(pathValue)

      while (result?.length > 0) {
        let key = '[' + result[0].substring(1, result[0].length - 1) + '].'
        pathValue = pathValue.replace(result[0], key)
        result = regex.exec(pathValue)
      }

      // process pathPrefixExclude
      if (pathOptions.pathPrefixExclude) {
        // remove path value
        if (pathValue.indexOf(pathOptions.pathPrefixExclude + '.') === 0) {
          pathValue = pathValue.replace(pathOptions.pathPrefixExclude + '.', '')
        } else if (pathValue === pathOptions.pathPrefixExclude) {
          pathValue = ''
        }
      }

      // process (root)
      pathValue = pathValue.replace(/\(root\)/, '')
      if (pathValue.indexOf('.') === 0) {
        pathValue = pathValue.replace('.', '')
      }

      // process pathPrefixInclude
      if (pathOptions.pathPrefixInclude) {
        // include path value
        if (pathValue) {
          pathValue = pathOptions.pathPrefixInclude + '.' + pathValue
        } else {
          pathValue = pathOptions.pathPrefixInclude
        }
      }

      return pathValue
    }

    // check if jwt token timed out or if user just hasn't the correct rights for current api route
    if (err?.response?.status === 401) {
      // check if status 401 is jwt error or not enough rights
      singleCheckForExpiredToken()
    }

    if (err?.response?.data?.message) {
      // server error
      if (!options.hideSnackbar) {
        $snackbar(t('ERROR'), t(err.response.data.message), 'error', null, 6000)
      }
    } else if (err?.response?.data?.length > 0) {
      if (!options.hideSnackbar) {
        // show error message
        if (err.response.status === 422) {
          // validation error
          $snackbar(t('ERROR'), t('ERROR_SAVING_DATA'), 'error', null, 6000)
        } else {
          // loading error
          $snackbar(t('ERROR'), t('ERROR_LOADING_DATA'), 'error', null, 6000)
        }
      }

      // process errors
      err.response.data.forEach(item => {
        // check if error is required error
        if (item.message === 'required') {
          // build complete name of nested property with parent and property
          item.field = item.field ? item.field + '.' + item.details.property : item.details.property

          item.details.field = item.field
        }

        // process array path
        item.field = processArrayPath(item.field, options)
        if (item.details?.field) {
          item.details.field = processArrayPath(item.details.field, options)
        }

        // translate error message
        let message = (item.message || '').toLowerCase()
        let key = errorMapper[message]

        if (item.details && key) {
          item.message = t('field_server_validation.' + message, {expected: item.details[key]})
        } else {
          item.message = t('field_server_validation.' + message)
        }
      })

      // call server function
      if (fnSetServerError && typeof fnSetServerError === 'function') {
        fnSetServerError(err.response.data)
      }
    } else if (err) {
      // error message
      if (!options.hideSnackbar) {
        $snackbar(t('ERROR'), err, 'error', null, 6000)
      }
    } else {
      // default
      if (!options.hideSnackbar) {
        $snackbar(t('ERROR'), t('ERROR'), 'error', null, 6000)
      }
    }
  }

  /**
   * Show success snack bar
   *
   * @param {string} title The message to display (if text then this is header)
   * @param {string,undefined} text The description message to display
   */
  function showSuccess(title, text = '') {
    $snackbar(title, text, 'success', null, 6000)
  }

  /**
   * Show warning snack bar
   *
   * @param {string} title The message to display (if text then this is header)
   * @param {string,undefined} text The description message to display
   */
  function showWarning(title, text = '') {
    $snackbar(title, text, 'warning', null, 6000)
  }

  /**
   * Show error snack bar
   *
   * @param {string} text The message to display
   */
  function showError(text = '') {
    $snackbar(t('ERROR'), text, 'error', null, 6000)
  }

  /**
   * Delete property if value not in list
   *
   * @param array
   * @param data
   * @param field
   * @param options
   */
  function deleteValueIfNotInList(array, data, field, options = {}) {
    if (!array || !data || !field) {
      return
    }

    // check if empty list and not required field
    if (!options.isRequiredField && array.length < 1) {
      return
    }

    options.compareField = options.compareField || 'id'

    let found = false
    array.forEach(item => {
      if (typeof item === 'object') {
        // object value
        if (data[field] && item[options.compareField] === data[field][options.compareField]) {
          found = true
        }
      } else {
        // single value
        if (item === data[field]) {
          found = true
        }
      }
    })

    if (!found) {
      delete data[field]
    }
  }

  /**
   * Check custom keys for day
   *
   * @param {Object} ev
   */
  function customKeyCheckForDayInput(ev) {
    //keyCode < 48  = keyboard events
    //keyCode    8  = backspace
    //keyCode    9  = tab
    //keyCode   46  = delete
    //keyCode   37  = arrow left
    //keyCode   39  = arrow right
    //keyCode > 57 && keyCode < 96 = letter, windows, right click...
    //keyCode > 105 = num locks, F1 - F12
    //keyCode   188 = comma ","
    //keyCode   189 = minus "-"
    //keyCode   190 = Punkt "."
    //keyCode   109 = comma num lock
    //keyCode   110 = minus num lock

    if (ev.key === 'Dead') {
      ev.target.blur()
      setTimeout(() => ev.target.focus())
    }

    if ((ev.keyCode < 48 && ev.keyCode !== 8 && ev.keyCode !== 9 && ev.keyCode !== 46 && ev.keyCode !== 37 && ev.keyCode !== 39) || (ev.keyCode > 57 && ev.keyCode < 96) || (ev.keyCode > 105 && ev.keyCode !== 188 && ev.keyCode !== 189 && ev.keyCode !== 109 && ev.keyCode !== 110)) {
      ev.preventDefault()
    }
  }

  /**
   * Returns value by nested object path
   *
   * @param {Object} obj The data object
   * @param {string} path The path value separated by '.'
   * @returns {undefined|*}
   */
  function getValueByObjectPath(obj, path) {
    if (path) {
      if (path.indexOf('.') <= 0) {
        return obj[path]
      }

      const objectPath = path.split('.')
      for (let i = 0; i < objectPath.length; i++) {
        if (!obj[objectPath[i]]) {
          return
        }
        obj = obj[objectPath[i]]
      }
      return obj
    }
    return undefined
  }

  /**
   * Prepare the roles in getData to be disabled if active und required by another role
   *
   * @param {Array} list
   */
  function prepareListForRequiredRoles(list) {
    if (list && Array.isArray(list)) {
      list.forEach(item => {

        let disabled = []

        //suche alle abhängigen rollen und schreibe sie ins disabled array, als uniquen eintrag
        //damit erhält man eine einfache liste mit alles zu disableten einträgen
        item.roles.forEach(role => {
          if (role.active && role?.required_roles) {
            role.required_roles.forEach(req => {
              if (!disabled.includes(req)) {
                disabled.push(req)
              }
            })
          }
        })

        //laufe alle rollen durch und aktiviere sie bzw. disable sie wenn sie in der liste vorkommen
        item.roles.forEach(roleAg => {
          if (disabled.includes(roleAg.name)) {
            roleAg.disabled = true
            roleAg.active = true
          } else {
            roleAg.disabled = false
          }
        })
      })
    }
  }

  /**
   * Calculate physician display name by details
   *
   * @param {Object} data The user object with details
   * @returns {*|string}
   */
  function calculatePhysicianName(data) {
    let s = (data.last_name || '')
    if (data.title || data.first_name) {
      s += ','
    }
    if (data.title) {
      s += ' ' + data.title
    }
    if (data.first_name) {
      s += ' ' + data.first_name
    }
    if (data.additional_name) {
      s += ' (' + data.additional_name + ')'
    }
    return s
  }

  /**
   * Calculate user display name
   *
   * @param {Object} data The user object with details
   * @returns {*|string}
   */
  function calculateUserDisplayName(data) {
    if (!data.last_name && !data.first_name) {
      return data.email
    }

    return (data.last_name || '') + (data.first_name ? ', ' + data.first_name : '')
  }

  /**
   * Calculate BMI
   *
   * @param {number} height The height in cm
   * @param {number} weight The weight in kg
   * @returns {number}
   */
  function calculateBMI(height, weight) {
    height = (height || 0) / 100
    weight = weight || 0

    let bmi = weight / (height * height)

    return parseFloat(bmi.toFixed(2))
  }

  /**
   * Calculate skin surface
   *
   * @param  {number}height The height in cm
   * @param  {number}weight The weight in kg
   * @param {string} method The calculation method
   * @returns {number}
   */
  function calculateSkinSurface(height, weight, method) {
    let surface
    let skin_surface

    if (method === 'mosteller') {
      surface = Math.sqrt(weight * height / 3600)
      skin_surface = parseFloat(surface.toFixed(2))
    } else if (method === 'dubois') {
      surface = Math.pow(weight, 0.425) * Math.pow(height, 0.725) * 71.84 / 10000
      skin_surface = parseFloat(surface.toFixed(2))
    }

    return skin_surface
  }

  /**
   * Calculate creatinine clearance
   *
   * @param  {number} options.weight The weight in kg
   * @param  {number} options.creatinine Creatinine value
   * @param  {string} options.creatinineMethod The calculation method
   * @param  {number} options.skinSurface Skin surface value
   * @param  {string} options.gender Gender of patient
   * @param  {Date} options.dateOfBirth Date of birth
   * @returns {number|null}
   */
  function calculateCreatinineClearance(options) {
    if (!options.weight || !options.creatinine || !options.creatinineMethod || !options.skinSurface || !options.gender || !options.dateOfBirth) {
      return null
    }

    const age = getAge(options.dateOfBirth, new Date())
    let genderFactor = 1
    let creatinineClearance = 0

    if (options.creatinineMethod === 'cockcroft') {
      if (options.gender === 'w') {
        genderFactor = 0.85
      }

      creatinineClearance = ((140 - age) * options.weight / (options.creatinine * 72)) * genderFactor
    }

    if (options.creatinineMethod === 'jellife') {
      if (options.gender === 'w') {
        genderFactor = 0.9
      }

      creatinineClearance = (((98 - ((age - 20) * 0.8)) * options.skinSurface) / (options.creatinine * 1.73)) * genderFactor
    }

    return parseFloat(creatinineClearance.toFixed(2))
  }

  /**
   * Get age of patient
   *
   * @param {Date} dateOfBirth
   * @param {Date} dateOfDeath optional
   * @returns {number} Returns the age
   */
  function getAge(dateOfBirth, dateOfDeath = new Date()) {
    const d = new Date(dateOfBirth)
    const now = dateOfDeath ? new Date(dateOfDeath) : new Date()

    let years = now.getFullYear() - d.getFullYear()

    const currentMonth = now.getMonth()
    const currentDay = now.getDate()

    const birthMonth = d.getMonth()
    const birthDay = d.getDate()

    if (currentMonth < birthMonth || currentMonth === birthMonth && currentDay < birthDay) {
      years--
    }
    return Math.floor(years)
  }

  /**
   * Get gender details by gender abbreviation
   *
   * @param {string} gender
   * @returns {{color: string, icon: string, text: string, value: string}|{color: string, icon: string, text: string, value: string}|{color: string, icon: string, text: string, value: string}|{color: string, icon: string, text: string, value: string}|{color: string, icon: string, text: string, value: string}|{}}
   */
  function getGender(gender) {
    let a = formsMetaData.genderValues.filter(item => item.value === gender)
    return a[0] || {}
  }

  /**
   * Returns if unit is relative unit
   *
   * @param {string} schemaUnit The unit value
   * @returns {boolean}
   */
  function isSchemaUnitRelative(schemaUnit) {
    return schemaUnit === 'mg/m²' || schemaUnit === 'mg/kg' || schemaUnit === 'µg/kg' || schemaUnit === 'AUC'
  }

  /**
   * Calculate absolute dosage
   *
   * @param {number} dosage
   * @param {string} unit
   * @param {Object} currentVitalData
   * @returns {{dosage: number, unit: string}}
   */
  function calcAbsoluteDosage(dosage, unit, currentVitalData) {
    // agentUnitValues: ['AUC', 'mg/m²', 'mg/kg', 'g', 'mg', 'µg', 'ml', 'mmol', 'I.E.', 'Mio. I.E.', 'µg/kg']

    // weight
    if (unit === 'mg/kg') {
      return {dosage: Math.round(dosage * currentVitalData.weight * 100) / 100, unit: 'mg'}
    }
    if (unit === 'µg/kg') {
      return {dosage: Math.round(dosage * currentVitalData.weight * 100) / 100, unit: 'µg'}
    }
    // kof
    if (unit === 'mg/m²') {
      return {dosage: Math.round(dosage * currentVitalData.skin_surface * 100) / 100, unit: 'mg'}
    }
    // AUC
    if (unit === 'AUC') {
      return {dosage: Math.round(dosage * (25 + currentVitalData.creatinine_clearance) * 100) / 100, unit: 'mg'}
    }

    return {dosage: Math.round(dosage * 100) / 100, unit: unit}
  }

  /**
   * Returns the short weekday name of given date
   *
   * @param {string} dateStr
   * @returns {string}
   */
  function getDayName(dateStr) {
    let locale = 'de-DE'
    let date = new Date(dateStr)
    return date.toLocaleDateString(locale, {weekday: 'short'})
  }

  /**
   * Returns true if date is on weekend, otherwise false
   *
   * @param {string} dateStr
   * @returns {boolean}
   */
  function dateIsNotWorkday(dateStr) {
    let dayOfWeek = new Date(dateStr).getDay()
    return (dayOfWeek === 6) || (dayOfWeek === 0)
  }

  /**
   * Setting hours, minutes, seconds, and milliseconds to zero,
   *
   * @param {Date} date
   * @returns {Date}
   */
  function resetTimeOfDate(date) {
    const resetDate = new Date(date)

    resetDate.setHours(0, 0, 0, 0)

    return resetDate
  }

  /**
   * Rounds down a value to a given integer interval
   * examples:
   *   - roundDown(37.5, 10) => 30
   *   - roundDown(37.5, 5) => 35
   *   - roundDown(37.5, 6) => 36
   *
   * @param value {number} The value to round down
   * @param interval {number} The interval to round down to
   * @returns {number}
   */
  function roundDown(value, interval) {
    return Math.trunc(value / interval) * interval
  }

  /**
   * Returns filter for icd 10 range
   *
   * @param {string} value
   * @returns {array}
   */
  function getIcdRangeFilter(value) {
    value = value.toUpperCase()

    return [
      {'icds.from.code': value.substring(0, value.indexOf('.'))},
      {'icds.from.code': {$regex: value, $options: 'i'}},
      {'icds.from.code': value},
      {icds: {$elemMatch: {'from.code': {'$lte': value}, 'from.code_range': {'$gte': value}}}},
      {icds: {$elemMatch: {'from.code': {'$lte': value}, 'to.code_range': {'$gte': value}}}}
    ]
  }

  /**
   * Calculate address single line from object
   *
   * @param {Object} data The address object
   * @returns {string}
   */
  function calcAddress(data) {
    let s = ''

    if (data.street) {
      s += data.street
    }

    if (data.number) {
      s += ' ' + data.number
    }

    if (s.length > 0) {
      s += ','
    }

    if (data.zip) {
      s += ' ' + data.zip
    }

    if (data.city) {
      s += ' ' + data.city
    }

    return s
  }

  /**
   * Set container height in tab views, has to be calculated
   *
   * @param {number} tabindex
   * @param {number} tabsCount
   */
  function setContainerHeight(tabindex, tabsCount = 0) {
    let headerSpecial = document.getElementsByClassName('lx-layout-content-stick-header-special')
    let valHead = headerSpecial[0]?.getBoundingClientRect()

    //ACHTUNG: auch wenn die folgenden elemente mit getElementById aufgerufen werden könnten geht das nicht weil die tabs per v-show eingebunden sind
    //damit kommt id mehrfach vor
    let header = document.getElementsByClassName('lx-layout-content-stick-header')
    let scroller = document.getElementsByClassName('lx-content-scroll-container')
    let footer = document.getElementsByClassName('lx-layout-content-stick-footer')

    //befindet sich in der app.vue
    let wind = document.getElementsByClassName('lx-layout-static-background')

    if (header?.length > 0 && scroller?.length > 0 && footer?.length > 0) {
      if (header?.length > 0 && valHead) {
        for (let i = 0; i < header.length; i++) {
          let tab = header[i].getAttribute('data-tab')

          if (tab && tab === tabindex.toString() && scroller?.length > 0) {
            header[i].style.top = (valHead.top + valHead.height) + 'px'
            scroller[i].style.marginTop = (valHead.top) + 'px'
            scroller[i].style.minHeight = (wind[0].getBoundingClientRect().height - scroller[i].getBoundingClientRect().top) - (wind[0].getBoundingClientRect().height - footer[i].getBoundingClientRect().top) + 'px'
          }
        }
      }
    }
  }

  /**
   * Copies value to clipboard
   *
   * @param {string} value
   */
  function copyValueToClipboard(value = '') {
    navigator.clipboard.writeText(value)
      .then(() => {
        showSuccess(t('messages.success_saved_to_clipboard'))
      })
      .catch(err => {
        console.log(err)
      })
  }

  /**
   * Deep clones data using structuredClone, if it fails it uses JSON parse and stringify
   *
   * @param {Object} value The item to deep clone
   * @returns {Object}
   */
  function cloneData(value) {
    try {
      return structuredClone(toRaw(value))
    } catch (e) {
      if (e.name === 'DataCloneError') {
        return JSON.parse(JSON.stringify(value))
      } else {
        throw e
      }
    }
  }

  /**
   * Get application routes in a list grouped in most, average, hardly
   *
   * @param applicationRoutes
   * @returns {({type: string, label: string}|{type: string, label: string}|{type: string, label: string})[]}
   */
  function getApplicationRoutesGrouped(applicationRoutes = []) {
    applicationRoutes.sort((a, b) => {
      return a.name.toUpperCase() < b.name.toUpperCase() ? -1 : 1
    })

    let parts = {
      most: [{type: 'divider', label: t('metadata.frequency_groups.most')}],
      average: [{type: 'divider', label: t('metadata.frequency_groups.average')}],
      hardly: [{type: 'divider', label: t('metadata.frequency_groups.hardly')}]
    }

    applicationRoutes.forEach(item => {
      if (item.frequency_group && parts[item.frequency_group]) {
        parts[item.frequency_group].push({id: item.id, name: item.name})
      }
    })

    return [
      ...parts['most'],
      ...parts['average'],
      ...parts['hardly']
    ]
  }

  /**
   *  Set focus
   */
  function setAutofocus(formName) {
    // set focus on filter input
    nextTick(() => {
      let getFieldForFocus = document.getElementById(formName)
      if (getFieldForFocus) {
        getFieldForFocus.focus()
      }
    })
  }

  /**
   *  Create Label
   *
   *  @param therapyDetails
   */
  function createLabel(therapyDetails) {
    let label = therapyDetails.agents.map(a => `${a.dosage.toFixed(2).replace('.', ',')} ${a.unit} ${a.agent.name} ${a.manufacturer?.Langname ? `(${a.manufacturer.Langname}) ` : ''}`).join(', ')
    if (therapyDetails.carrier_solution.name !== formsMetaData.constants.undiluted) {
      label += 'in ' + therapyDetails.total_volume.toFixed(2).replace('.', ',') + ' ml'
    }
    label += ' ' + therapyDetails.carrier_solution.name

    switch (therapyDetails.application_form.name) {
      case 'Infusion':
        label +=' Inf.-Beutel'
        break
      case 'Dauerinfusion':
        label +=' Elastomerpumpe'
        break
      case 'Injektion':
        label +=' Bolus'
        break
      case 'Blaseninstillation':
        label +=' Leerbeutel'
        break
      case 'Spüllösung':
        label +=' Spritze'
        break
      case 'Zuspritzung':
        label +=' Spritze'
        break
      case 'Augentropfen':
        label +=' Tropfflasche'
        break
    }

    label += ' (1x) ' + LxUtils.formatDateFromISODateString(therapyDetails.application_date) + ' Dj'
    return label
  }

  return {
    calculateDays,
    calculatePhysicianName,
    calculateUserDisplayName,
    checkArrayEquals,
    customKeyCheckForDayInput,
    deepEqual,
    deleteValueIfNotInList,
    getQueryParam,
    processQueryParams,
    getValueByObjectPath,
    prepareListForRequiredRoles,
    makeId,
    processErrors,
    showSuccess,
    showWarning,
    showError,
    calculateBMI,
    calculateSkinSurface,
    calculateCreatinineClearance,
    getAge,
    getGender,
    isSchemaUnitRelative,
    calcAbsoluteDosage,
    calculateCycleIndexByDate,
    getDayName,
    resetTimeOfDate,
    dateIsNotWorkday,
    roundDown,
    getIcdRangeFilter,
    calcAddress,
    setContainerHeight,
    copyValueToClipboard,
    cloneData,
    getApplicationRoutesGrouped,
    setAutofocus,
    createLabel
  }
}
